import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import en, { TranslationDict } from '../i18n/en';
import fr from '../i18n/fr';

export type Locale = 'en' | 'fr';

const LANG_KEY = 'zoningpal_lang';
const BANNER_KEY = 'zoningpal_ca_banner_dismissed';

type Dictionaries = Record<Locale, TranslationDict>;

const dictionaries: Dictionaries = { en, fr };

type LanguageContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: TranslationDict;
  translate: (value: string, vars?: Record<string, string | number>) => string;
  bannerDismissed: boolean;
  dismissBanner: () => void;
  showBanner: () => void;
};

const LanguageContext = createContext<LanguageContextValue | null>(null);

function readStoredLocale(): Locale {
  try {
    const stored = localStorage.getItem(LANG_KEY);
    if (stored === 'fr' || stored === 'en') return stored;
  } catch (_) {
    // ignore
  }
  return 'en';
}

function readBannerDismissed(): boolean {
  try {
    return localStorage.getItem(BANNER_KEY) === '1';
  } catch (_) {
    return false;
  }
}

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, key) =>
    vars[key] != null ? String(vars[key]) : ''
  );
}

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = useState<Locale>(() => readStoredLocale());
  const [bannerDismissed, setBannerDismissed] = useState<boolean>(() => readBannerDismissed());

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      localStorage.setItem(LANG_KEY, next);
    } catch (_) {
      // ignore
    }
  }, []);

  const dismissBanner = useCallback(() => {
    setBannerDismissed(true);
    try {
      localStorage.setItem(BANNER_KEY, '1');
    } catch (_) {
      // ignore
    }
  }, []);

  const showBanner = useCallback(() => {
    setBannerDismissed(false);
    try {
      localStorage.removeItem(BANNER_KEY);
    } catch (_) {
      // ignore
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale === 'fr' ? 'fr' : 'en';
  }, [locale]);

  const t = dictionaries[locale];

  const translate = useCallback(
    (value: string, vars?: Record<string, string | number>) => interpolate(value, vars),
    []
  );

  const value = useMemo(
    () => ({
      locale,
      setLocale,
      t,
      translate,
      bannerDismissed,
      dismissBanner,
      showBanner,
    }),
    [locale, setLocale, t, translate, bannerDismissed, dismissBanner, showBanner]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = (): LanguageContextValue => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return ctx;
};
